"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { useAuth } from "@/contexts/AuthContext";

const NUDGE_SNOOZE_KEY = "fnp_nudge_snoozed_until";
const NUDGE_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const NUDGE_DELAY_MS = 3000;

/**
 * Personalization upsell nudge. Shows ONLY when the visitor has already made
 * a cookie decision of Decline All or Essentials only — never before a first
 * choice, and never while the main banner is open.
 *
 * Opt In performs the exact same action as Agree and Proceed.
 */
export function PersonalizationNudge() {
  const { ready, status, isBannerVisible, acceptAll } = useCookieConsent();

  // Mounted under AuthProvider (see app/providers.tsx).
  const { user } = useAuth();
  const authedUserId = user?.id ?? undefined;

  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!ready || isBannerVisible) return;
    if (status !== "rejected" && status !== "essentials") return;
    // Shows on every page load while declined/essentials — only the explicit
    // 7-day dismiss snooze suppresses it.
    try {
      const snoozedUntil = Number(
        window.localStorage.getItem(NUDGE_SNOOZE_KEY) ?? 0,
      );
      if (snoozedUntil > Date.now()) return;
    } catch {
      return;
    }
    const t = window.setTimeout(() => setShow(true), NUDGE_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [ready, isBannerVisible, status]);

  // A fresh Agree/accept hides the nudge immediately.
  useEffect(() => {
    if (status === "accepted" || status === "custom") setShow(false);
  }, [status]);

  if (!show) return null;

  // Dismiss snoozes the nudge for 7 days (a snooze, not a choice).
  const dismiss = () => {
    try {
      window.localStorage.setItem(
        NUDGE_SNOOZE_KEY,
        String(Date.now() + NUDGE_SNOOZE_MS),
      );
    } catch {
      /* storage unavailable — nudge will simply re-show next load */
    }
    setShow(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-modal="true"
      aria-label="Personalization opt-in"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
    >
      <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-4 text-center shadow-2xl sm:p-6">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss personalization nudge"
          className="absolute right-3 top-3 rounded-full px-2 py-1 text-lg leading-none text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
        >
          ×
        </button>
        <h2 className="px-6 text-base font-semibold text-gray-900">
          Unlock personalized property matches
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
          Opt in to personalization and we&apos;ll tailor property matches to
          you and improve your search experience across the site.{" "}
          <Link
            href="/cookie-policy"
            className="font-medium text-emerald-700 underline underline-offset-2"
          >
            View cookie policy
          </Link>
          .
        </p>
        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              acceptAll(authedUserId);
              setShow(false);
            }}
            className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800"
          >
            Personalize My Experience
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-500 transition hover:bg-gray-50"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

export default PersonalizationNudge;
